import OpenSeaDragon from "openseadragon";
import { createOSDAnnotator } from "@annotorious/openseadragon";
import "@annotorious/openseadragon/annotorious-openseadragon.css";
import {
  Box,
  Center,
  Group,
  Text,
  Title,
  Textarea,
  Button,
  ActionIcon,
  useMantineTheme,
  useMantineColorScheme,
} from "@mantine/core";
import { IconCamera, IconTrash } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "utils/helper";
import ImageControlToolbar from "./ImageControlToolbar";
import { CaptureToolbar } from "core/components/index";
import { logger } from "utils/logger";
import { CaptureEventType } from "core/components/capture/CaptureThumb";
import { MarkupEventTypes } from "utils/constants";
import classes from "./SingleLayout.module.scss";

const SingleLayout = ({
  selectedAnnotation,
  image,
  settings,
  onImageClick,
  imageControlSettings,
  instanceId = "openSeaDragon",
  imageLocHash,
  onCaptureChange,
  showSettings = true,
  showToolbar = true,
  toolbarStyle = {},
  onViewportChange,
  onViewerReady,
  settingsChange,
  onMarkupChange,
  user,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  // const [imageHeight, setImageHeight ] = useState(200)
  const containerRef = useRef();
  const imageRef = useRef();

  const [viewer, setViewer] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [comment, setComment] = useState("");
  const anno = useRef(null);
  const annotations = useRef([]);

  const zoomWrapper = useRef(null);
  const controlRef = useRef(null);
  const [showTagTalent, setShowTagTalent] = useState(false);
  const commentPopoverRef = useRef(null);
  const selectedAnnotationRef = useRef(null);
  const textareaRef = useRef(null);
  const [commentVisible, setCommentVisible] = useState(false);
  const spacebarPressedRef = useRef(false);
  const [spacebarPressed, setSpacebarPressed] = useState(false);
  var init = useRef(false);
  
  // Ref to store current image to avoid closure issues in annotation event handlers
  const currentImageRef = useRef(image);

  const updatePopoverPosition = (annotation) => {
    if (!viewer || !annotation?.target?.selector) return;
    const selector = annotation.target.selector;
    if (selector.geometry) {
      const geo = selector.geometry;
      let x, y, width, height;
      if (geo.bounds) {
        x = geo.x || geo.bounds.minX || 0;
        y = geo.y || geo.bounds.minY || 0;
        width = geo.w || geo.bounds.maxX - geo.bounds.minX || 0;
        height = geo.h || geo.bounds.maxY - geo.bounds.minY || 0;
      } else {
        x = geo.x || 0;
        y = geo.y || 0;
        width = geo.w || geo.width || 0;
        height = geo.h || geo.height || 0;
      }

      const container = zoomWrapper.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const viewportBounds = viewer.viewport.getBounds();
      const scale = containerRect.width / viewportBounds.width;

      const POPOVER_WIDTH = 300;
      const POPOVER_HEIGHT = commentPopoverRef.current
        ? commentPopoverRef.current?.getBoundingClientRect().height
        : 150; // get the real height of the textarea + margins

      // Calculate bottom position (default - below annotation)
      const pointBottom = viewer.viewport.imageToViewportCoordinates(
        new OpenSeaDragon.Point(x + width / 2, y + height)
      );
      const leftBottom = (pointBottom.x - viewportBounds.x) * scale;
      const topBottom = (pointBottom.y - viewportBounds.y) * scale + 10;

      // Calculate top position (alternative - above annotation)
      const pointTop = viewer.viewport.imageToViewportCoordinates(
        new OpenSeaDragon.Point(x + width / 2, y)
      );
      const leftTop = (pointTop.x - viewportBounds.x) * scale;
      const topTop =
        (pointTop.y - viewportBounds.y) * scale - POPOVER_HEIGHT - 10;

      // Check if bottom position is within container (with some margin)
      const bottomFits =
        topBottom + POPOVER_HEIGHT + 20 <= containerRect.height;

      let finalLeft, finalTop;

      if (bottomFits) {
        // Use bottom position
        finalLeft = leftBottom;
        finalTop = topBottom;
      } else {
        // Use top position
        finalLeft = leftTop;
        finalTop = topTop;
      }

      // Center the popover horizontally on the annotation
      const centerLeft = finalLeft - POPOVER_WIDTH / 2;

      // Constrain horizontally to stay within container
      const constrainedLeft = Math.max(
        20,
        Math.min(centerLeft, containerRect.width - POPOVER_WIDTH - 20)
      );

      // Constrain vertically to stay within container
      const constrainedTop = Math.max(
        20,
        Math.min(finalTop, containerRect.height - POPOVER_HEIGHT)
      );

      // Constrain bottom to stay within container
      const constrainedBottom = Math.max(
        20,
        Math.min(finalTop, containerRect.height - POPOVER_HEIGHT)
      );

      setPopoverPosition({
        left: constrainedLeft,
        top: constrainedTop,
      });
    }
  };

  useEffect(() => {
    logger.log("IMAGE CHANGE");
    const imageMatch = (img1, img2) => {
      let str1 = img1.split("?")[0];
      let str2 = img2.split("?")[0];
      logger.log(str1, str2);
      return str1 === str2;
    };
    if (image && viewer) {
      var imageUrl = image.preview;
      let sourceUrl = viewer.source ? viewer.source.url : "";
      if (!imageMatch(sourceUrl, imageUrl)) {
        logger.log(viewer.tileSources.url, imageUrl);
        // if(imageLocHash && imageLocHash.current && imageLocHash.current[image.id] && imageLocHash.current[image.id] === image.previewMid ){
        //     imageUrl = imageLocHash.current[image.id];
        // }

        viewer.open({
          type: "image",
          url: imageUrl,
        });
      }
    }

    // Update annotations if annotorious is initialized
    if (anno.current && image) {
      anno.current.clearAnnotations();
      anno.current.cancelSelected();
      var annotHash = {};
      if (image.annotations && image.annotations.length > 0) {
        image.annotations.forEach((annotation) => {
          if (annotation.payload?.markup) {
            if (
              annotHash[
                annotation.payload?.markup?.id ||
                  annotation.payload?.payload?.id
              ]
            ) {
              return;
            }
            var markup = annotation.payload.markup
              ? annotation.payload.markup
              : annotation.payload.payload;
            anno.current.addAnnotation(markup);
            annotHash[annotation.payload?.markup?.id] = markup;
          }
        });
      }
    }

    annotations.current = image?.annotations ? [...image.annotations] : [];
    
    // Update the image ref whenever image changes
    currentImageRef.current = image;
  }, [image, viewer]);

  useEffect(() => {

    logger.log("selectedAnnotation", selectedAnnotation);
    if (!selectedAnnotation) {
      setCommentVisible(false);
      anno.current?.setSelected(null);
      return;
    }
    if (anno.current && selectedAnnotation) {
      const id = selectedAnnotation?.payload?.markup?.id;
      if (id) {
        anno.current.setSelected(id);
        setComment(
          selectedAnnotation?.payload?.markup?.bodies?.find(
            (b) => b.purpose === "commenting"
          )?.value || ""
        );

        // showCommentPopover(selectedAnnotation);
        updatePopoverPosition(selectedAnnotation?.payload?.markup);
        setTimeout(() => {
         

          setTimeout(() => {
            setCommentVisible(true);
          }, 200);
          

          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
            }
          }, 300);

        }, 200);
      }
    }
  }, [selectedAnnotation]);

  const InitOpenseadragon = useCallback(() => {
    if (viewer) {
      if (viewer.zoomInButton?.tracker) viewer.zoomInButton.tracker.destroy();
      if (viewer.zoomOutButton?.tracker) viewer.zoomOutButton.tracker.destroy();
      if (viewer.zoomInButton) viewer.zoomInButton.destroy();
      viewer && viewer.destroy();
      if (controlRef.current) controlRef.current.remove();
    }

    const container = document.getElementById(`${instanceId}`);
    if (
      !container ||
      container.offsetHeight === 0 ||
      container.offsetWidth === 0
    ) {
      logger.log("Container not ready, retrying...");
      setTimeout(InitOpenseadragon, 100);
      return;
    }

    if (!image || !image.preview) {
      logger.log("Image not available, retrying...");
      setTimeout(InitOpenseadragon, 100);
      return;
    }

    logger.log("Initializing viewer...");

    const initViewer = OpenSeaDragon({
      id: `${instanceId}`,
      prefixUrl: "openseadragon/images/",
      animationTime: 0.2,
      blendTime: 0.1,
      constrainDuringPan: false,
      maxZoomPixelRatio: 5,
      visibilityRatio: 1,
      zoomPerScroll: 1.1,
      crossOriginPolicy: "Anonymous",
      gestureSettingsMouse: {
        clickToZoom: false,
        dragToPan: true,
        pinchToZoom: true,
      },
      tileSources: {
        type: "image",
        url: image ? image.preview : "",
      },
      zoomInButton: `${instanceId}-zoom-in`,
      zoomOutButton: `${instanceId}-zoom-out`,
      homeButton: `${instanceId}-home`,
    });

    setViewer(initViewer);
    onViewerReady && onViewerReady(initViewer);

    // Helper functions for comment popover
    const updatePopoverPosition = (annotation) => {
      if (!initViewer || !annotation?.target?.selector) return;
      const selector = annotation.target.selector;
      if (selector.geometry) {
        const geo = selector.geometry;
        let x, y, width, height;
        if (geo.bounds) {
          x = geo.x || geo.bounds.minX || 0;
          y = geo.y || geo.bounds.minY || 0;
          width = geo.w || geo.bounds.maxX - geo.bounds.minX || 0;
          height = geo.h || geo.bounds.maxY - geo.bounds.minY || 0;
        } else {
          x = geo.x || 0;
          y = geo.y || 0;
          width = geo.w || geo.width || 0;
          height = geo.h || geo.height || 0;
        }

        const container = zoomWrapper.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const viewportBounds = initViewer.viewport.getBounds();
        const scale = containerRect.width / viewportBounds.width;

        const POPOVER_WIDTH = 300;
        const POPOVER_HEIGHT = commentPopoverRef.current
          ? commentPopoverRef.current?.getBoundingClientRect().height
          : 150; // get the real height of the textarea + margins

        // Calculate bottom position (default - below annotation)
        const pointBottom = initViewer.viewport.imageToViewportCoordinates(
          new OpenSeaDragon.Point(x + width / 2, y + height)
        );
        const leftBottom = (pointBottom.x - viewportBounds.x) * scale;
        const topBottom = (pointBottom.y - viewportBounds.y) * scale + 10;

        // Calculate top position (alternative - above annotation)
        const pointTop = initViewer.viewport.imageToViewportCoordinates(
          new OpenSeaDragon.Point(x + width / 2, y)
        );
        const leftTop = (pointTop.x - viewportBounds.x) * scale;
        const topTop =
          (pointTop.y - viewportBounds.y) * scale - POPOVER_HEIGHT - 10;

        // Check if bottom position is within container (with some margin)
        const bottomFits =
          topBottom + POPOVER_HEIGHT + 20 <= containerRect.height;

        let finalLeft, finalTop;

        if (bottomFits) {
          // Use bottom position
          finalLeft = leftBottom;
          finalTop = topBottom;
        } else {
          // Use top position
          finalLeft = leftTop;
          finalTop = topTop;
        }

        // Center the popover horizontally on the annotation
        const centerLeft = finalLeft - POPOVER_WIDTH / 2;

        // Constrain horizontally to stay within container
        const constrainedLeft = Math.max(
          20,
          Math.min(centerLeft, containerRect.width - POPOVER_WIDTH - 20)
        );

        // Constrain vertically to stay within container
        const constrainedTop = Math.max(
          20,
          Math.min(finalTop, containerRect.height - POPOVER_HEIGHT)
        );

        // Constrain bottom to stay within container
        const constrainedBottom = Math.max(
          20,
          Math.min(finalTop, containerRect.height - POPOVER_HEIGHT)
        );

        setPopoverPosition({
          left: constrainedLeft,
          top: constrainedTop,
        });
      }
    };

    const showCommentPopover = (annotation) => {
      // setSelectedAnnotation(annotation);
      // onAnnotationEvent(AnnotationEventTypes.SELECT_ANNOTATION,annotation);
      // selectedAnnotationRef.current = annotation;
      // const commentBody = annotation.bodies?.find(
      //   (b) => b.purpose === "commenting"
      // );
      // setComment(commentBody?.value || "");
      // updatePopoverPosition(annotation);
      // setTimeout(() => {
      //   if (textareaRef.current) {
      //     textareaRef.current.focus();
      //   }
      // }, 200);
    };

    // Initialize Annotorious if commenting is enabled
    if (settings?.isCommenting && onMarkupChange && user) {
      const annotatorConfig = {
        crosshair: false,
        widgets: [{ widget: "COMMENT" }],
        drawingEnabled: isDrawingEnabled(settings),
        drawingTool: settings.annotationType?.toLowerCase() || "rect", // 'rect' or 'polygon'
        userSelectAction: "EDIT",
        drawingMode:'drag'
      };

      const annotator = createOSDAnnotator(initViewer, annotatorConfig);
      anno.current = annotator;

      // annotator.setDrawingEnabled(isDrawingEnabled(settings));

      // Set the drawing tool based on annotationType
      if (settings.annotationType) {
        const tool = settings.annotationType.toLowerCase();
        if (tool === "polygon" || tool === "rectangle") {
          annotator.setDrawingTool(tool);
        }
      }

      // Event handlers - use currentImageRef.current to avoid closure issues
      annotator.on("createAnnotation", (annotation) => {
        logger.log("Annotation created:", annotation);
        if (onMarkupChange) {
          onMarkupChange(MarkupEventTypes.ADD_MARKUP, {
            image: currentImageRef.current,
            payload: annotation,
          });
        }
        if (settings.isCommenting) {
          setTimeout(() => {
            if (annotator) {
              // annotator.setDrawingEnabled(isDrawingEnabled(settings));
            }
          }, 100);
        }
        showCommentPopover(annotation);
      });

      annotator.on("updateAnnotation", (annotation) => {
        logger.log("Annotation updated:", annotation);
        if (onMarkupChange) {
          const existingAnnotation = annotations.current.find(
            (item) => item.payload?.markup?.id === annotation.id
          );
          const updatedData = { ...existingAnnotation, payload: annotation };
          onMarkupChange(MarkupEventTypes.UPDATE_MARKUP, {
            image: currentImageRef.current,
            payload: updatedData.payload,
          });
        }
      });
      annotator.on("selectionChanged", (annotations) => {
        console.log("Selected annotations", annotations);
        // if there are no annotations selected, cancel the comment
        if (annotations.length === 0) {
          handleCommentCancel();
        }
      });
      annotator.on("selectAnnotation", (annotation) => {
        logger.log("Annotation selected:", annotation);
        // annotator.setDrawingEnabled(false);
      });

      annotator.on("clickAnnotation", (annotation) => {
        logger.log("Annotation clicked");
        // annotator.setDrawingEnabled(false);

        if (onMarkupChange) {
          // find the annotation from the
          onMarkupChange(MarkupEventTypes.SELECT_MARKUP, {
            image: currentImageRef.current,
            payload: annotation,
          });
        }
        showCommentPopover(annotation);
      });

      annotator.on("deleteAnnotation", () => {
        logger.log("Annotation deleted");
        if (onMarkupChange) {
          onMarkupChange(MarkupEventTypes.DESELECT_MARKUP, {
            image: currentImageRef.current,
            payload: null,
          });
        }
        selectedAnnotationRef.current = null;
        setComment("");
        if (settings.isCommenting) {
          // annotator.setDrawingEnabled(isDrawingEnabled(settings));
        }
      });

      initViewer.addHandler("viewport-change", () => {
        if (selectedAnnotationRef.current) {
          updatePopoverPosition(selectedAnnotationRef.current);
        }
      });
    }

    // move the controls to the top nav only if they don't alread exist.
    var controllerCol = document.getElementById("controllerCol");
    // var colQry = controllerCol.querySelector(`#${instanceId}-controller`);
    // check if controllerCol contains item of id ${instanceId}-controller
    if (
      controllerCol &&
      !controllerCol.querySelector(`#${instanceId}-controller`)
    ) {
      controllerCol.append(controlRef.current);
    } else {
      // remove item of id ${instanceId}-controller from this viewer containerRef
      var elem = containerRef.current.querySelector(
        `#${instanceId}-controller`
      );
      var toolbarQry = containerRef.current.querySelector(
        `#${instanceId}-controller`
      );
      if (elem) {
        elem.remove();
      }
    }
  }, [
    image,
    instanceId,
    viewer,
    settings?.isCommenting,
    settings?.annotationType,
    onMarkupChange,
    user,
  ]);

  useEffect(() => {
    if (!init.current && image) {
      InitOpenseadragon();
      // setIsInit(true);
      init.current = true;
    }
  }, [image, InitOpenseadragon]);

  useEffectOnce(() => {
    if (!init.current && image) {
      InitOpenseadragon();
      // setIsInit(true);
      init.current = true;
    }

    return () => {
      viewer && viewer.zoomInButton.destroy();
      viewer && viewer.zoomOutButton.destroy();
      viewer && viewer.destroy();
      //controlRef.current && document.getElementById('openseacontroller').remove();

      var elem = document.getElementById(`${instanceId}-controller`);
      if (elem) {
        elem.remove();
      }

      if (viewer && viewer.zoomInButton) {
        viewer.zoomInButton.destroy();
      }

      logger.log("CLEANUP");
    };
  }, []);

  // Update drawing state when settings change
  useEffect(() => {
    if (anno.current) {
      anno.current.setDrawingEnabled(isDrawingEnabled(settings));
    }
  }, [settings?.isCommenting, settings?.annotationType]);

  // Update drawing tool when annotationType changes
  useEffect(() => {
    if (anno.current && settings?.annotationType) {
      const tool = settings.annotationType.toLowerCase();
      if (tool === "polygon" || tool === "rectangle") {
        anno.current.setDrawingTool(tool);
      }
    }
  }, [settings?.annotationType]);

  // Handle comment submission
  const handleCommentOk = useCallback(() => {
    if (selectedAnnotation && anno.current && onMarkupChange) {

      logger.log("selectedAnnotation", anno.current.getSelected());
      const liveAnnotation = anno.current.getSelected();
      const updatedAnnotation = {
        ...selectedAnnotation,
        payload: {
          ...selectedAnnotation.payload,
          markup: {
            ...liveAnnotation[0],
            bodies: [
              ...(selectedAnnotation.payload.markup.bodies || []).filter(
                (b) => b.purpose !== "commenting"
              ),
              {
                type: "TextualBody",
                purpose: "commenting",
                value: comment,
              },
            ],
          },
        },
      };

      anno.current.updateAnnotation(updatedAnnotation.payload.markup);

      const existingAnnotation = annotations.current.find(
        (item) => item.payload.id === updatedAnnotation.payload.markup.id
      );

      const updatedData = {
        ...existingAnnotation,
        payload: updatedAnnotation,
      };

      // onMarkupChange(MarkupEventTypes.UPDATE_MARKUP, {
      //   image,
      //   payload: updatedAnnotation,
      // });

      if (onMarkupChange) {
        onMarkupChange(MarkupEventTypes.DESELECT_MARKUP, {
          image: currentImageRef.current,
          payload: null,
        });
      }
      // selectedAnnotationRef.current = null;
      setComment("");
      logger.log("Comment added:", comment);
    }
  }, [selectedAnnotation, comment, onMarkupChange]);

  // Handle comment cancellation
  const handleCommentCancel = useCallback(() => {
    if (onMarkupChange) {
      if (anno.current) {
        // Get all annotations currently in Annotorious
        const allAnnotations = anno.current.getAnnotations();

        // Get all saved annotation IDs from the image
        const savedAnnotationIds = new Set();
        if (annotations.current.length > 0) {
          annotations.current.forEach((annotation) => {
            const markupId =
              annotation.payload?.markup?.id || annotation.payload?.payload?.id;
            if (markupId) {
              savedAnnotationIds.add(markupId);
            }
          });
        }

        // Loop over all Annotorious annotations and delete ones that aren't saved
        allAnnotations.forEach((annotation) => {
          if (!savedAnnotationIds.has(annotation.id)) {
            // This annotation exists in Annotorious but hasn't been saved
            anno.current.removeAnnotation(annotation.id);
          }
        });
      }
      onMarkupChange(MarkupEventTypes.DESELECT_MARKUP, {
        image: currentImageRef.current,
        payload: null,
      });
    }

    selectedAnnotationRef.current = null;
    anno.current.setSelected(null);

    setComment("");

    // if (anno.current && settings?.isCommenting) {
    //   anno.current.setDrawingEnabled(isDrawingEnabled(settings));
    // }

  }, [
    settings?.isCommenting,
    onMarkupChange,
    selectedAnnotation,
    annotations.current,
  ]);

  // Cancel in-progress drawing
  const cancelDrawing = useCallback(() => {
    if (!anno.current || !settings?.isCommenting) return;

    // Get all annotations currently in Annotorious
    const allAnnotations = anno.current.getAnnotations();

    // Get all saved annotation IDs from the image
    const savedAnnotationIds = new Set();
    if (annotations.current.length > 0) {
      annotations.current.forEach((annotation) => {
        const markupId =
          annotation.payload?.markup?.id || annotation.payload?.payload?.id;
        if (markupId) {
          savedAnnotationIds.add(markupId);
        }
      });
    }

    // Remove any unsaved annotations (in-progress drawings that were created)
    allAnnotations.forEach((annotation) => {
      if (!savedAnnotationIds.has(annotation.id)) {
        anno.current.removeAnnotation(annotation.id);
      }
    });

    // Cancel any selected annotation or in-progress drawing
    anno.current.cancelSelected();

    // Force cancel any active drawing by temporarily disabling and re-enabling drawing
    // This ensures any in-progress drawing (like dragging a rectangle) is cancelled
    if (settings?.isCommenting) {
      // anno.current.setDrawingEnabled(false);
      // Use setTimeout to ensure the disable takes effect before re-enabling
      setTimeout(() => {
        if (anno.current && settings?.isCommenting) {
          // anno.current.setDrawingEnabled(isDrawingEnabled(settings));
        }
      }, 100);
    }
  }, [settings?.isCommenting, settings?.annotationType]);

  // useEffect(() => {
  //   if (anno.current) {
  //     anno.current.setDrawingEnabled(isDrawingEnabled(settings));
  //   }
  // }, [settings?.annotationType, settings?.isCommenting]);

  useEffect(() => {
    if (!viewer || !onViewportChange) return;

    let panTimeout = null;
    let zoomTimeout = null;

    const handleViewportChange = (event) => {
      const zoom = viewer.viewport.getZoom();
      const center = viewer.viewport.getCenter();
      const rotation = viewer.viewport.getRotation();

      onViewportChange({
        zoom,
        center: { x: center.x, y: center.y },
        rotation,
        sourceInstanceId: instanceId,
        eventType: event.type,
      });
    };

    const handlePan = () => {
      // Minimal debounce for pan events
      if (panTimeout) clearTimeout(panTimeout);
      panTimeout = setTimeout(() => {
        handleViewportChange({ type: "pan" });
      }, 8); // Reduced from 16ms to 8ms for faster response
    };

    const handleZoom = () => {
      // Minimal debounce for zoom events
      if (zoomTimeout) clearTimeout(zoomTimeout);
      zoomTimeout = setTimeout(() => {
        handleViewportChange({ type: "zoom" });
      }, 4); // Reduced from 8ms to 4ms for faster response
    };

    // Listen to viewport changes
    viewer.addHandler("zoom", handleZoom);
    viewer.addHandler("pan", handlePan);
    viewer.addHandler("rotate", handleViewportChange);

    return () => {
      if (panTimeout) clearTimeout(panTimeout);
      if (zoomTimeout) clearTimeout(zoomTimeout);
      viewer.removeHandler("zoom", handleZoom);
      viewer.removeHandler("pan", handlePan);
      viewer.removeHandler("rotate", handleViewportChange);
    };
  }, [viewer, onViewportChange, instanceId]);

  // Handle Escape key to cancel in-progress drawing
  useEffect(() => {
    if (!anno.current || !settings?.isCommenting) return;

    // Handle Escape key to cancel drawing
    const handleKeyDown = (e) => {
      // Only handle Escape if we're not in a textarea/input and drawing is enabled
      if (
        e.key === "Escape" &&
        e.target.tagName !== "TEXTAREA" &&
        e.target.tagName !== "INPUT"
      ) {
        // If there's a selected annotation, just cancel the selection
        if (selectedAnnotation) {
          e.preventDefault();
          e.stopPropagation();
          anno.current.cancelSelected();
          return;
        }

        // Otherwise, try to cancel any in-progress drawing
        // Always try to cancel drawing if Escape is pressed
        // This handles cases where drawing is in progress but not yet in the annotations list
        e.preventDefault();
        e.stopPropagation();
        cancelDrawing();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anno.current, settings?.isCommenting, selectedAnnotation, cancelDrawing]);

  // Handle Spacebar modifier to temporarily disable drawing for panning/zooming
  useEffect(() => {
    if (!anno.current || !settings?.isCommenting) return;

    const handleKeyDown = (e) => {
      // Only handle Spacebar if we're not in a textarea/input and drawing is enabled
      if (
        e.key === " " &&
        e.target.tagName !== "TEXTAREA" &&
        e.target.tagName !== "INPUT" &&
        !spacebarPressedRef.current
      ) {
        e.preventDefault(); // Prevent page scroll
        spacebarPressedRef.current = true;
        setSpacebarPressed(true);
        
        // Disable drawing to allow panning/zooming
        if (anno.current && isDrawingEnabled(settings)) {
          anno.current.setDrawingEnabled(false);
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === " " && spacebarPressedRef.current) {
        e.preventDefault();
        spacebarPressedRef.current = false;
        setSpacebarPressed(false);
        
        // Re-enable drawing if it should be enabled
        if (anno.current && isDrawingEnabled(settings)) {
          anno.current.setDrawingEnabled(isDrawingEnabled(settings));
        }
      }
    };

    // Handle case where spacebar is released outside the window (e.g., user switches tabs)
    const handleBlur = () => {
      if (spacebarPressedRef.current) {
        spacebarPressedRef.current = false;
        setSpacebarPressed(false);
        if (anno.current && isDrawingEnabled(settings)) {
          anno.current.setDrawingEnabled(isDrawingEnabled(settings));
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      
      // Cleanup: ensure drawing is restored if spacebar was pressed
      if (spacebarPressedRef.current && anno.current && isDrawingEnabled(settings)) {
        anno.current.setDrawingEnabled(isDrawingEnabled(settings));
      }
    };
  }, [anno.current, settings?.isCommenting, settings?.annotationType]);

logger.log(anno.current)
  return (
    <Box
      className={classes.container}
      spacing={"lg"}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
      ref={containerRef}
    >

      {/* <Text style={{position:'absolute', top:0, left:0, zIndex:1000}}>{isDrawingEnabled(settings).toString()}</Text> */}
      {image ? (
        <div
          ref={zoomWrapper}
          className={`layoutImage ${classes.layoutImage} ${
            isDrawingEnabled(settings) ? classes.annotating : ""
          } ${spacebarPressed ? classes.spacebarPressed : ""}`}
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showToolbar ? (
            <div
              ref={controlRef}
              id={`${instanceId}-controller`}
              className="openseacontrols"
              style={toolbarStyle}
            >
              <ImageControlToolbar
                instanceId={instanceId}
                settings={settings}
                settingsChange={settingsChange}
              />
            </div>
          ) : null}
          <div
            className={classes.container}
            id={`${instanceId}`}
            style={{
              flexGrow: 1,
              width: "100%",
            }}
          ></div>
        </div>
      ) : (
        <Center style={{ height: "100%" }}>
          <div style={{ textAlign: "center", opacity: 0.5 }}>
            <IconCamera size={48} stroke={1} />
            <Title color={"dimmed"} order={3}>
              Select an Image
            </Title>
          </div>
        </Center>
      )}

      {/* Comment Popover */}
      {selectedAnnotation && commentVisible && (
        <Box
          ref={commentPopoverRef}
          className={classes.commentPopover}
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <Textarea
            className={classes.commentTextarea}
            ref={textareaRef}
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                handleCommentCancel();
              }
            }}
            autosize
            minRows={2}
            maxRows={6}
          />
          {/* <Box>
            <Text size="xs">{popoverPosition.top}px</Text>
            <Text size="xs">{popoverPosition.left}px</Text>
          </Box> */}
          <Group
            justify="flex-end"
            align="stretch"
            mt="sm"
            style={{
              width: "100%",
            }}
          >
            {/* <ActionIcon
              size="md"
              variant="default"
              onClick={handleCommentCancel}
            >
              <IconTrash size={16} />
            </ActionIcon> */}

            <Group>
              <Button size="xs" variant="default" onClick={handleCommentCancel}>
                Cancel
              </Button>
              <Button size="xs" variant="filled" onClick={handleCommentOk}>
                Save
              </Button>
            </Group>
          </Group>
        </Box>
      )}

      {/* <ImageThumb onClick={onImageClick} /> */}
      {/* <div sx={{flexGrow:1, flexDirection:'column'}}> */}

      {/* <div style={{ width: '100%', marginLeft: 'auto', marginRight: 'auto', flexGrow:1,  }}>
                
                {image?
                    <BackgroundImage className='layoutImage' sx={{height:'100%',backgroundSize:'contain', backgroundRepeat:'no-repeat', border:'1px solid transparent'}}
                    src={image?image.filePath:''}
                    radius="sm" />
                :
                    <Center sx={{height:'100%'}}>
                        <div style={{textAlign:'center'}}>
                        <IconCamera size={48} stroke={1} />
                        <Title color={'dimmed'} order={3}>Select an Image</Title>
                        </div>
                    </Center>
                } 
                

            </div> */}

      {/* <Image src={tmpURL} ref={imageRef}
                // width={'100%'}
                // style={{flexGrow:0}}
                height="200"
                width="200"
                fit="contain"
                /> */}

      {/* </div> */}
      {image && showSettings ? (
        <Group
          justify="center"
          px={"xs"}
          py={"xs"}
          className={classes.settingsContainer}
          style={{ width: imageRef.current?.outerWidth }}
        >
          <Group justify="apart">
            <Text size={"xs"}>{image.name}</Text>
            {/* <TagTalent
              opened={showTagTalent}
              onChange={setShowTagTalent}
              captures={[image]}
            > */}
            <div>
              <CaptureToolbar
                onCaptureChange={(type, data) => {
                  onCaptureChange(type, data, [image]);
                  if (type === CaptureEventType.TAG_TALENT) {
                    setShowTagTalent(true);
                  }
                }}
                rating={image.star}
                status={image.sak}
              />
            </div>
            {/* </TagTalent> */}
          </Group>
        </Group>
      ) : null}
    </Box>
  );
};

export default SingleLayout;


const isDrawingEnabled = (settings) => {
  return settings.isCommenting && settings.annotationType !== "PAN";
}